import React from 'react';
import { motion } from 'framer-motion';
import { Check, Clock, Lock, ShieldCheck, Zap } from 'lucide-react';
import { books } from '../data/books';
import { BookCover3D } from './BookCover3D';
import { CtaButton } from './CtaButton';
interface HeroProps {
  copiesLeft: number;
}
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 26
  },
  show: {
    opacity: 1,
    y: 0
  }
};
const ease = [0.22, 1, 0.36, 1] as const;
export function Hero({
  copiesLeft
}: HeroProps) {
  return <header className="relative overflow-hidden bg-noir-950 pb-14 pt-9 sm:pb-24 sm:pt-16 lg:pb-28 lg:pt-20" data-id="element-122" data-mp="1274odq5sli7r">
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 left-1/2 h-[620px] w-[820px] -translate-x-1/2 rounded-full bg-wine-800/40 blur-[130px]" data-id="element-123" data-mp="sbb39ie0cged" />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 noise opacity-70" data-id="element-124" data-mp="nekmesudpgrw" />

      <div className="container relative" data-id="element-125" data-mp="382p4d9lap7jk">
        <motion.div initial="hidden" animate="show" transition={{
        staggerChildren: 0.09,
        delayChildren: 0.05
      }} className="mx-auto flex max-w-4xl flex-col items-center text-center" data-id="element-126" data-mp="380vxpuaomnbk">
          <motion.p variants={fadeUp} transition={{
          duration: 0.6,
          ease
        }} className="inline-flex items-center gap-2.5 rounded-full border border-gold-500/30 bg-gold-500/[0.06] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-widest2 text-gold-400" data-id="element-127" data-mp="3o59efq3thihp">
            <span className="h-1.5 w-1.5 rounded-full bg-crimson-500 animate-pulseDot" data-id="element-128" data-mp="358rcygz62489" />
            Solo para personas que toman decisiones inteligentes
          </motion.p>

          <motion.h1 variants={fadeUp} transition={{
          duration: 0.7,
          ease
        }} className="mt-5 font-display text-[1.95rem] font-bold leading-[1.1] tracking-tight text-cream sm:mt-7 sm:text-5xl sm:leading-[1.08] lg:text-[3.5rem]" data-id="element-129" data-mp="2qlofgaaufvkx">
            Si No Sabes Leer a una Persona, Atraerla o Conectar de Verdad,{' '}
            <span className="relative inline-block text-crimson-500" data-id="element-130" data-mp="s2b89zesml5o">
              Estás Perdiendo
              <span aria-hidden="true" className="absolute -bottom-1 left-0 h-[3px] w-full bg-crimson-600/60" data-id="element-131" data-mp="nb1jt24zdnvd" />
            </span>{' '}
            a las Mejores Personas de Tu Vida
          </motion.h1>

          <motion.p variants={fadeUp} transition={{
          duration: 0.6,
          ease
        }} className="mt-4 max-w-2xl text-[15px] leading-relaxed text-cream/65 sm:mt-7 sm:text-lg" data-id="element-132" data-mp="db2dlbw1a9lo">
            5 guías que te dan todo lo que necesitas saber sobre atracción, seducción,
            lenguaje corporal, primeras citas y conversación real — todo en un solo bundle
            por menos de lo que gastas en una cena.
          </motion.p>

          <motion.div variants={fadeUp} transition={{
          duration: 0.6,
          ease
        }} className="mt-1 w-full sm:mt-4" data-id="element-133" data-mp="3lu081ftmtv6l">
            <HeroStack data-id="element-134" data-mp="1x9b6f48dr7e5" />
            <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-widest2 text-gold-400/80 sm:mt-1 sm:text-[11px]" data-id="element-135" data-mp="1xa429a5swchi">
              5 volúmenes · Acceso instantáneo
            </p>
          </motion.div>

          <motion.div variants={fadeUp} transition={{
          duration: 0.6,
          ease
        }} className="mt-7 flex w-full max-w-md flex-col items-center rounded-2xl border border-gold-500/20 bg-noir-800/80 p-5 text-center backdrop-blur-sm sm:mt-10 sm:p-7" data-id="element-136" data-mp="268y3jdu2tr1e">
            <span className="text-[11px] font-medium uppercase tracking-widest2 text-cream/45 sm:text-xs" data-id="element-137" data-mp="2ky4352wttj9t">
              Valor real: <span className="line-through" data-id="element-138" data-mp="2p1s8rg1we4sf">$137.00 USD</span>
            </span>
            <div className="mt-1.5 flex items-end justify-center gap-3 sm:mt-2" data-id="element-139" data-mp="2tyiqs8x9jagh">
              <span className="font-display text-[3.25rem] font-bold leading-none text-cream sm:text-7xl" data-id="element-140" data-mp="umzh1s44xy5r">
                $9.99
              </span>
              <span className="pb-1.5 text-sm font-medium text-cream/50 sm:pb-2" data-id="element-141" data-mp="zlsuxlnc3r6g">USD</span>
            </div>
            <span className="mt-3.5 inline-flex items-center gap-2 rounded-full bg-crimson-600/15 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wider text-crimson-500 ring-1 ring-crimson-500/35 sm:mt-4 sm:text-xs" data-id="element-142" data-mp="4dgyix4pnr96">
              <Zap className="h-3.5 w-3.5" aria-hidden="true" data-id="element-143" data-mp="9ah1v0a6efwl" />
              Ahorras $127.01 — 93% de descuento
            </span>
          </motion.div>

          <motion.div variants={fadeUp} transition={{
          duration: 0.6,
          ease
        }} className="mt-6 flex w-full flex-col items-center sm:mt-8" data-id="element-144" data-mp="3hqsco67lzvix">
            <CtaButton className="w-full sm:w-auto" data-id="element-145" data-mp="3rgvgz2k6kvwk">
              <Check className="h-5 w-5" aria-hidden="true" data-id="element-146" data-mp="3mpdhkixt33dp" />
              Sí, Quiero los 5 Ebooks Ahora → Solo $9.99
            </CtaButton>
            <ul className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11px] text-cream/50 sm:mt-5 sm:gap-x-5 sm:text-xs" data-id="element-147" data-mp="2yyraatvmln4r">
              <li className="flex items-center gap-1.5" data-id="element-148" data-mp="2tyvbd91sclw2">
                <Lock className="h-3.5 w-3.5 text-gold-500" aria-hidden="true" data-id="element-149" data-mp="19hvqj0qyvbe3" />
                Pago seguro
              </li>
              <li className="flex items-center gap-1.5" data-id="element-150" data-mp="1ebgpow4aqbev">
                <Zap className="h-3.5 w-3.5 text-gold-500" aria-hidden="true" data-id="element-151" data-mp="zliu3v41ejfn" />
                Acceso inmediato
              </li>
              <li className="flex items-center gap-1.5" data-id="element-152" data-mp="14ju4q825oxyx">
                <ShieldCheck className="h-3.5 w-3.5 text-gold-500" aria-hidden="true" data-id="element-153" data-mp="1s8vjed1l15cn" />
                Sin suscripción
              </li>
              <li className="flex items-center gap-1.5" data-id="element-154" data-mp="1x7giua90fl01">
                <Clock className="h-3.5 w-3.5 text-crimson-500" aria-hidden="true" data-id="element-155" data-mp="1j98e3g5qffeb" />
                Solo {copiesLeft} copias
              </li>
            </ul>
          </motion.div>
        </motion.div>
      </div>
    </header>;
}
function HeroStack() {
  const layout = [{
    rotate: -14,
    x: '-84%',
    y: 30,
    z: 10,
    scale: 0.84,
    opacity: 0.75
  }, {
    rotate: -7,
    x: '-44%',
    y: 12,
    z: 20,
    scale: 0.92,
    opacity: 0.9
  }, {
    rotate: 0,
    x: '0%',
    y: 0,
    z: 30,
    scale: 1,
    opacity: 1
  }, {
    rotate: 7,
    x: '44%',
    y: 12,
    z: 20,
    scale: 0.92,
    opacity: 0.9
  }, {
    rotate: 14,
    x: '84%',
    y: 30,
    z: 10,
    scale: 0.84,
    opacity: 0.75
  }];
  const order = [0, 1, 4, 3, 2];
  return <div className="relative mx-auto flex h-[225px] w-full max-w-[640px] items-center justify-center sm:h-[400px] lg:h-[440px]" data-id="element-156" data-mp="1zsnymomgs1zd">
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-8 bottom-10 h-32 rounded-[50%] bg-crimson-600/20 blur-[70px]" data-id="element-157" data-mp="1vrcf90u6oczm" />
      <div className="perspective-[1400px] relative h-full w-full" data-id="element-158" data-mp="1lswqh4impfta">
        {order.map((bookIndex, stackIndex) => {
        const book = books[bookIndex];
        const style = layout[bookIndex];
        return <div key={book.id} style={{
          zIndex: style.z
        }} className="absolute left-1/2 top-1/2 w-[30%] -translate-x-1/2 -translate-y-1/2 sm:w-[27%]" data-id="element-159" data-mp="3g7fs74vcjfa9" data-mpi={stackIndex}>
              <motion.div initial={{
            opacity: 0,
            x: '0%',
            y: 60,
            rotate: 0,
            scale: 0.9
          }} animate={{
            opacity: style.opacity,
            x: style.x,
            y: style.y,
            rotate: style.rotate,
            scale: style.scale
          }} transition={{
            duration: 0.9,
            delay: 0.25 + stackIndex * 0.1,
            ease
          }} data-id="element-160" data-mp="3jpmzm9ijwntn">
                <div className="animate-float" style={{
              animationDelay: `${stackIndex * 0.5}s`
            }} data-id="element-161" data-mp="34qt20sxkmco8">
                  <BookCover3D src={book.cover} alt={`Portada de ${book.title}`} intensity={bookIndex === 2 ? 14 : 8} loading={bookIndex === 2 ? 'eager' : 'lazy'} highPriority={bookIndex === 2} data-id="element-162" data-mp="39t0i9upfoewd" />
                </div>
              </motion.div>
            </div>;
      })}
      </div>
    </div>;
}